import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { SESSION_STORAGE_KEYS } from '../session_storage/constants/session-storage-keys.constant';
import { OrganizationContext, OrganizationContextService } from './organization-context.service';
import {
  clearStoredAccountSelection,
  persistStoredAccountSelection,
  readStoredAccountHomeId,
  resolveAccountForTenant,
} from './account-selection.util';
import { setMsalTenantId } from './msal.factory';

/** Recovery belongs only to this attempt, never to a newer switch, logout or tab. */
export class AuthSwitchAttempt {
  private readonly previousTenant = localStorage.getItem(SESSION_STORAGE_KEYS.TENANT_ID);
  private readonly previousAccount: AccountInfo | null;
  private readonly previous: OrganizationContext;
  private readonly generation: number;
  private expectedTenant = this.previousTenant;
  private expectedHome = readStoredAccountHomeId(this.previousTenant);
  private retainedHome = this.expectedHome;

  constructor(
    private readonly msal: MsalService,
    private readonly context: OrganizationContextService,
    targetTenant: string,
  ) {
    const identity = context.snapshot.identity;
    this.previousAccount = identity
      ? (msal.instance
          .getAllAccounts()
          .find(
            (account) => account.tenantId === identity.tenantId && account.homeAccountId === identity.homeAccountId,
          ) ?? null)
      : resolveAccountForTenant(msal.instance.getAllAccounts(), msal.instance.getActiveAccount(), this.previousTenant);
    this.previous = context.beginSwitch(targetTenant);
    this.generation = context.snapshot.generation;
  }

  selectTenant(tenantId: string): void {
    this.assertCurrent();
    const home = readStoredAccountHomeId(tenantId);
    localStorage.setItem(SESSION_STORAGE_KEYS.TENANT_ID, tenantId);
    this.expectedTenant = tenantId;
    this.expectedHome = home;
    this.retainedHome = this.expectedHome;
    setMsalTenantId(tenantId);
  }

  selectAccount(account: AccountInfo): void {
    this.assertCurrent();
    this.msal.instance.setActiveAccount(account);
    persistStoredAccountSelection(account);
    this.expectedHome = account.homeAccountId;
    this.retainedHome = this.expectedHome;
  }

  clearSelection(tenantId: string): void {
    this.assertCurrent();
    clearStoredAccountSelection(tenantId);
    if (tenantId === this.expectedTenant) this.expectedHome = null;
  }

  assertCurrent(account?: AccountInfo): void {
    if (
      this.context.snapshot.generation !== this.generation ||
      this.context.snapshot.phase !== 'switching' ||
      localStorage.getItem(SESSION_STORAGE_KEYS.TENANT_ID) !== this.expectedTenant ||
      readStoredAccountHomeId(this.expectedTenant) !== this.expectedHome ||
      (account && readStoredAccountHomeId(account.tenantId) !== account.homeAccountId)
    ) {
      throw new Error('Organization context changed during authentication');
    }
  }

  recover(): void {
    try {
      this.assertCurrent();
    } catch {
      return;
    }
    try {
      // Keep the person's preference even if cache cleanup removed their profile.
      // A later retry must request sign-in, not silently pick somebody else.
      if (this.expectedTenant && this.retainedHome) {
        persistStoredAccountSelection({ tenantId: this.expectedTenant, homeAccountId: this.retainedHome });
      }
      // A destructive cache operation can fail after removing the old account.
      // Never recreate that account from our saved object or select another person.
      const available =
        this.previousAccount &&
        this.msal.instance
          .getAllAccounts()
          .find(
            (account) =>
              account.tenantId === this.previousAccount.tenantId &&
              account.homeAccountId === this.previousAccount.homeAccountId,
          );
      if (!available || !this.previousTenant || this.previous.phase === 'failed') {
        this.msal.instance.setActiveAccount(null);
        this.context.failSwitch(this.generation);
        return;
      }
      this.msal.instance.setActiveAccount(available);
      localStorage.setItem(SESSION_STORAGE_KEYS.TENANT_ID, this.previousTenant);
      persistStoredAccountSelection(available);
      setMsalTenantId(this.previousTenant);
      this.context.restore(this.previous);
    } catch {
      // Recovery can itself encounter unavailable browser storage/MSAL cache.
      // Keep a retryable closed gate rather than masking the original failure.
      this.context.failSwitch(this.generation);
    }
  }
}
